import React from 'react';
// Import from 'react-native' for consistency - Vite aliases this to 'react-native-web'
import type { ViewStyle, TextStyle } from 'react-native';
import { View as RNView, Text as RNText } from 'react-native';

// Import the existing CSS file so styles are available
import './index.css';

interface HybridViewProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'style'> {
  className?: string;
  style?: ViewStyle | React.CSSProperties;
  children?: React.ReactNode;
}

interface HybridTextProps extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'style'> {
  className?: string;
  style?: TextStyle | React.CSSProperties;
  children?: React.ReactNode;
  accessibilityRole?: string;
  accessibilityLevel?: number;
}

// For elements with className, render as regular divs/spans with CSS classes
// For elements without className, use React Native Web components
export const HybridView = React.forwardRef<HTMLDivElement, HybridViewProps>(
  ({ className, style, children, ...props }, ref) => {
    if (className) {
      // Render as a regular div with the CSS class
      return (
        <div ref={ref} className={className} style={style as React.CSSProperties} {...props}>
          {children}
        </div>
      );
    }
    // Render as React Native Web View
    return (
      <RNView
        ref={ref as unknown as React.Ref<React.ElementRef<typeof RNView>>}
        style={style as ViewStyle}
        {...(props as React.ComponentProps<typeof RNView>)}
      >
        {children}
      </RNView>
    );
  }
);

export const HybridText: React.FC<HybridTextProps> = ({
  className,
  style,
  children,
  accessibilityRole,
  accessibilityLevel,
  ...props
}) => {
  if (className) {
    // Render as a regular span with the CSS class
    return (
      <span
        className={className}
        style={style as React.CSSProperties}
        role={accessibilityRole}
        aria-level={accessibilityLevel}
        {...props}
      >
        {children}
      </span>
    );
  }
  // Render as React Native Web Text
  return (
    <RNText
      style={style as TextStyle}
      accessibilityRole={
        accessibilityRole as React.ComponentProps<typeof RNText>['accessibilityRole']
      }
      {...(props as React.ComponentProps<typeof RNText>)}
    >
      {children}
    </RNText>
  );
};

// Export with simpler names
export const View = HybridView;
export const Text = HybridText;
